import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Calendar, Clock, AlertTriangle, Users } from 'lucide-react';
import { testApi } from '@/lib/api';
import { Test } from '@/types';
import { TestSchedulingStep } from '@/components/tests/TestSchedulingStep';
import { useToast } from '@/components/ui/use-toast';

const TestScheduleEditPage = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [modificationValidation, setModificationValidation] = useState<any>(null);

  useEffect(() => {
    if (testId) {
      loadTest();
      loadModificationValidation();
    }
  }, [testId]);

  const loadTest = async () => {
    try {
      setLoading(true);
      const response = await testApi.getActualTestById(testId!);
      setTest(response);
    } catch (error) {
      console.error('Failed to load test:', error);
      toast({
        title: "Error",
        description: "Failed to load test details",
        variant: "destructive",
      });
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const loadModificationValidation = async () => {
    try {
      const validation = await testApi.validateTestModification(testId!);
      setModificationValidation(validation);
    } catch (error) {
      console.error('Failed to load modification validation:', error);
      // Don't show error toast for validation failure, just log it
    }
  };

  const handleScheduleSubmit = async (scheduleData: any) => {
    if (!test) return;

    // Check if students have already taken the test
    if (modificationValidation && !modificationValidation.canModify) {
      toast({
        title: "Cannot Modify Test",
        description: modificationValidation.reason || "Students have already taken this test. Please create a new test instead.",
        variant: "destructive",
      });
      return;
    }

    try {
      await testApi.scheduleTest(test.id, scheduleData);
      toast({
        title: "Success",
        description: "Test scheduled successfully",
      });
      navigate('/tests');
    } catch (error) {
      console.error('Failed to schedule test:', error);
      toast({
        title: "Error",
        description: "Failed to schedule test",
        variant: "destructive",
      });
    }
  };

  const handleBack = () => {
    navigate('/tests');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading test details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-muted-foreground">Test not found</p>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tests
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tests
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Schedule Test</h1>
          <p className="text-muted-foreground">Configure scheduling options for your test</p>
        </div>
      </div>

      {/* Test Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Test Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Title</p>
              <p className="font-medium">{test.title}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Subject</p>
              <p className="font-medium">{test.subjects?.[0]?.subjectName || test.subjectName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Questions</p>
              <p className="font-medium">{test.totalQuestions}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Duration</p>
              <p className="font-medium flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {test.duration} minutes
              </p>
            </div>
          </div>
          {test.description && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{test.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modification Warnings */}
      {modificationValidation && (
        <div className="mb-6">
          {!modificationValidation.canModify && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Cannot modify this test:</strong> {modificationValidation.reason}
                <br />
                <span className="text-sm">
                  Students have already taken this test ({modificationValidation.studentParticipationCount} participants).
                  Please create a new test instead.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {modificationValidation.canModify && modificationValidation.isInProgress && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> This test is currently in progress.
                <br />
                <span className="text-sm">
                  Modifying the test schedule while students are taking it may disrupt the test and confuse students.
                  Proceed with caution.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {modificationValidation.canModify && modificationValidation.studentParticipationCount > 0 && !modificationValidation.isInProgress && (
            <Alert className="mb-4">
              <Users className="h-4 w-4" />
              <AlertDescription>
                <strong>Note:</strong> {modificationValidation.studentParticipationCount} students have already taken this test.
                <br />
                <span className="text-sm">
                  You can still modify the schedule, but consider the impact on students who have already completed the test.
                </span>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Scheduling Form */}
      <Card>
        <CardHeader>
          <CardTitle>Scheduling Options</CardTitle>
        </CardHeader>
        <CardContent>
          {modificationValidation && !modificationValidation.canModify ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Test Cannot Be Modified</h3>
              <p className="text-muted-foreground mb-4">
                This test cannot be modified because students have already taken it.
              </p>
              <Button onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Tests
              </Button>
            </div>
          ) : (
            <TestSchedulingStep
              onNext={handleScheduleSubmit}
              onBack={handleBack}
              showBackButton={false}
              nextButtonText="Schedule Test"
              isStandalone={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TestScheduleEditPage;
